// usePushNotifications.ts
// Custom React hook for managing push notification popup state and logic

import { useState, useEffect, useCallback, useRef } from "react";
import {
  requestPushPermission,
  getNotificationPermission,
  isNotificationBlocked,
} from "services/push.service";
import {
  PERMISSION_STATUS,
  STORAGE_KEYS,
  POPUP_DELAY_MS,
  REJECTION_COOLDOWN_MS,
} from "@/constants/notification";
import { isAppleDevice } from "helpers/userAgent";
import { isStandalone } from "core/utils/utils.helper";

export type PermissionStatus =
  | typeof PERMISSION_STATUS.UNKNOWN // Haven't checked yet
  | typeof PERMISSION_STATUS.GRANTED // Permission granted
  | typeof PERMISSION_STATUS.DENIED // User denied (can ask again later)
  | typeof PERMISSION_STATUS.BLOCKED // Permanently blocked (must enable manually)
  | typeof PERMISSION_STATUS.UNSUPPORTED // Browser doesn't support notifications
  | typeof PERMISSION_STATUS.PENDING; // Currently requesting permission

export interface UsePushNotificationsReturn {
  shouldShowPermissionPopup: boolean;
  shouldShowBlockedPopup: boolean;
  handleAllow: () => Promise<void>;
  handleReject: () => void;
  handleDismissForever: () => void;
  permissionStatus: PermissionStatus;
  retryCheckPermission: () => Promise<void>;
}

/**
 * Custom hook to manage push notification popup display and permission handling
 * Uses Klaviyo for push subscription management
 * @param userLoggedIn - Boolean indicating if user is logged in
 * @param userId - User account ID for Klaviyo identification
 * @param userEmail - User email for Klaviyo identification
 * @returns Object containing popup state and handlers
 */
export function usePushNotifications(
  userLoggedIn: boolean,
  userId?: string,
  userEmail?: string,
): UsePushNotificationsReturn {
  const [shouldShowPermissionPopup, setShouldShowPermissionPopup] =
    useState(false);
  const [shouldShowBlockedPopup, setShouldShowBlockedPopup] = useState(false);
  const [permissionStatus, setPermissionStatus] = useState<PermissionStatus>(
    PERMISSION_STATUS.UNKNOWN,
  );

  // Track if initial check has been done
  const initialCheckDone = useRef(false);

  /**
   * Subscribe to Klaviyo push notifications for the current user.
   * Called when permission is already granted (e.g., existing users on login).
   * Always re-sends subscription data to Klaviyo to keep credentials fresh,
   * preventing push delivery failures from expired endpoints.
   */
  const subscribeToKlaviyo = useCallback(async () => {
    if (!userId || !userLoggedIn) {
      return;
    }

    try {
      // Always re-subscribe to refresh credentials with Klaviyo.
      // Browser push endpoints can expire silently after inactivity,
      // so we get a fresh subscription on every app load.
      await requestPushPermission(userId, userEmail);
      console.log("[Klaviyo] Subscription refreshed for existing user");
    } catch (error) {
      console.error("[Klaviyo] Error subscribing:", error);
    }
  }, [userId, userLoggedIn, userEmail]);

  /**
   * Check if user permanently dismissed the notification prompt
   */
  const isPermanentlyDismissed = useCallback((): boolean => {
    if (typeof window === "undefined") return false;

    try {
      const stored = localStorage.getItem(
        STORAGE_KEYS.PUSH_PERMANENTLY_DISMISSED,
      );
      return stored === "true";
    } catch (error) {
      console.error("Error reading permanently dismissed status:", error);
      return false;
    }
  }, []);

  /**
   * Check if we should show the popup based on rejection cooldown
   */
  const shouldShowPopup = useCallback((): boolean => {
    if (typeof window === "undefined") return false;

    try {
      // Don't show if permanently dismissed
      if (isPermanentlyDismissed()) {
        return false;
      }

      // Check browser notification permission directly
      const browserPermission = getNotificationPermission();
      if (browserPermission === PERMISSION_STATUS.GRANTED) {
        return false;
      }

      // If blocked at browser level, don't show regular popup
      if (browserPermission === PERMISSION_STATUS.DENIED) {
        return false;
      }

      // Check if user rejected recently
      const rejectedAtStr = localStorage.getItem(STORAGE_KEYS.PUSH_REJECTED_AT);
      if (rejectedAtStr) {
        const rejectedAt = parseInt(rejectedAtStr, 10);
        const now = Date.now();
        const timeSinceRejection = now - rejectedAt;

        // If less than 2 days since rejection, don't show popup
        if (timeSinceRejection < REJECTION_COOLDOWN_MS) {
          return false;
        }
      }

      return true;
    } catch (error) {
      console.error("Error checking popup display logic:", error);
      return false;
    }
  }, [isPermanentlyDismissed]);

  /**
   * Check if notifications are blocked and we should show instructions
   */
  const shouldShowBlockedMessage = useCallback(async (): Promise<boolean> => {
    if (typeof window === "undefined") return false;

    try {
      // Don't show if permanently dismissed
      if (isPermanentlyDismissed()) {
        return false;
      }

      // Don't show if already granted at browser level
      if (getNotificationPermission() === PERMISSION_STATUS.GRANTED) {
        return false;
      }

      // Check if blocked at browser level
      const blocked = await isNotificationBlocked();
      return blocked;
    } catch (error) {
      console.error("Error checking blocked status:", error);
      return false;
    }
  }, [isPermanentlyDismissed]);
  /**
   * Retry checking permission status (useful after user changes browser settings)
   */
  const retryCheckPermission = useCallback(async () => {
    console.log("first");
  }, []);

  /**
   * Handle user tapping "Allow" button
   */
  const handleAllow = useCallback(async () => {
    setPermissionStatus(PERMISSION_STATUS.PENDING);
    setShouldShowPermissionPopup(false);

    try {
      // Request push permission and subscribe to Klaviyo
      const granted = await requestPushPermission(userId, userEmail);

      if (granted) {
        // Clear any rejection timestamp
        localStorage.removeItem(STORAGE_KEYS.PUSH_REJECTED_AT);
        setPermissionStatus(PERMISSION_STATUS.GRANTED);
        setShouldShowBlockedPopup(false);

        console.log("[Klaviyo] Push permission granted and subscribed");
      } else {
        // Permission denied - check if it's blocked or just denied
        const blocked = await isNotificationBlocked();

        if (blocked) {
          setPermissionStatus(PERMISSION_STATUS.BLOCKED);
          setShouldShowBlockedPopup(true);
        } else {
          setPermissionStatus(PERMISSION_STATUS.DENIED);
          // Store rejection timestamp
          localStorage.setItem(
            STORAGE_KEYS.PUSH_REJECTED_AT,
            Date.now().toString(),
          );
        }

        console.log("[Klaviyo] Push permission denied by user");
      }
    } catch (error) {
      console.error("[Klaviyo] Error handling push permission:", error);

      // Check if error was due to blocking
      const blocked = await isNotificationBlocked();
      if (blocked) {
        setPermissionStatus(PERMISSION_STATUS.BLOCKED);
        setShouldShowBlockedPopup(true);
      } else {
        setPermissionStatus(PERMISSION_STATUS.DENIED);
        // Store rejection timestamp on error
        localStorage.setItem(
          STORAGE_KEYS.PUSH_REJECTED_AT,
          Date.now().toString(),
        );
      }
    }
  }, [userId, userEmail]);

  /**
   * Handle user tapping "Reject" or "Maybe Later" button
   */
  const handleReject = useCallback(() => {
    setShouldShowPermissionPopup(false);

    try {
      // Store rejection timestamp
      const now = Date.now();
      localStorage.setItem(STORAGE_KEYS.PUSH_REJECTED_AT, now.toString());

      console.log("Push permission rejected, will ask again in 2 days");
    } catch (error) {
      console.error("Error storing rejection timestamp:", error);
    }
  }, []);

  /**
   * Handle user permanently dismissing notification prompts
   */
  const handleDismissForever = useCallback(() => {
    try {
      localStorage.setItem(STORAGE_KEYS.PUSH_PERMANENTLY_DISMISSED, "true");
      setShouldShowPermissionPopup(false);
      setShouldShowBlockedPopup(false);

      console.log("Push notifications permanently dismissed by user");
    } catch (error) {
      console.error("Error storing permanent dismissal:", error);
    }
  }, []);

  /**
   * Effect: Determine if popups should be shown after login
   * Also handles existing users with permission already granted -
   * unregisters old SW, registers new one, and syncs token with DB
   */
  useEffect(() => {
    if (!userLoggedIn) {
      // User not logged in, hide popups
      setShouldShowPermissionPopup(false);
      setShouldShowBlockedPopup(false);
      return;
    }

    // Perform initial permission check
    const performInitialCheck = async () => {
      initialCheckDone.current = true;

      // iOS standalone devices use Firebase FCM token via native bridge.
      // No popup or Klaviyo subscription needed — the native app handles push.
      if (isAppleDevice() && isStandalone()) {
        console.log("[Push] iOS standalone detected - skipping Klaviyo flow");
        setPermissionStatus(PERMISSION_STATUS.GRANTED);
        setShouldShowPermissionPopup(false);
        setShouldShowBlockedPopup(false);
        return;
      }

      // Check browser notification permission directly
      const browserPermission = getNotificationPermission();
      console.log(
        "[Push] performInitialCheck ~ browserPermission:",
        browserPermission,
      );

      // For existing users who already granted permission:
      // Ensure they are subscribed to Klaviyo and clean up any old Firebase SWs
      if (browserPermission === PERMISSION_STATUS.GRANTED) {
        console.log(
          "[Klaviyo] Permission already granted - syncing subscription for existing user",
        );
        setPermissionStatus(PERMISSION_STATUS.GRANTED);
        setShouldShowPermissionPopup(false);
        setShouldShowBlockedPopup(false);

        // Subscribe to Klaviyo if not already subscribed
        await subscribeToKlaviyo();
        return;
      }

      // After status is updated, check if we should show popup
      const shouldShow = shouldShowPopup();
      console.log("[Push] performInitialCheck ~ shouldShow:", shouldShow);
      const shouldShowBlocked = await shouldShowBlockedMessage();
      console.log(
        "[Push] performInitialCheck ~ shouldShowBlocked:",
        shouldShowBlocked,
      );

      if (shouldShowBlocked) {
        // Show blocked message immediately if blocked
        setShouldShowBlockedPopup(true);
        setShouldShowPermissionPopup(false);
      } else if (shouldShow) {
        // Show regular popup after delay (Klaviyo SW is registered at app boot in main.tsx)
        const timer = setTimeout(() => {
          setShouldShowPermissionPopup(true);
        }, POPUP_DELAY_MS);

        return () => clearTimeout(timer);
      }
    };

    performInitialCheck();
  }, [
    userLoggedIn,
    userId,
    userEmail,
    shouldShowPopup,
    shouldShowBlockedMessage,
    subscribeToKlaviyo,
  ]);

  /**
   * Effect: Update blocked message visibility when permission status changes
   */
  useEffect(() => {
    const checkBlocked = async () => {
      if (
        permissionStatus === "blocked" &&
        userLoggedIn &&
        !isPermanentlyDismissed()
      ) {
        setShouldShowBlockedPopup(true);
      } else if (permissionStatus === "granted") {
        setShouldShowBlockedPopup(false);
      }
    };

    checkBlocked();
  }, [permissionStatus, userLoggedIn, isPermanentlyDismissed]);

  return {
    shouldShowPermissionPopup,
    shouldShowBlockedPopup,
    handleAllow,
    handleReject,
    handleDismissForever,
    permissionStatus,
    retryCheckPermission,
  };
}
